import { useSyncExternalStore } from "react";
import { APP_URLS, useAppTarget } from "@/app/hooks/useAppUrl";
import { useAppSelector } from "@/app/store/hooks";
import { getAttribution } from "./attribution";
import { hasTrackingConsent } from "./consent";
import { getOrCreateSessionUUID } from "./session";

/** Where an app link leads. */
export type AppLinkTarget = "web" | "ios" | "android";

/** Apple caps campaign tokens (`ct`) at 40 characters. */
const APPLE_CT_MAX = 40;
const WEB_APP_HOST = new URL(APP_URLS.web).host;

/**
 * Tells which app surface a URL opens.
 *
 * @param url - Absolute or relative URL.
 * @returns `web` for the web app, `android` / `ios` for the Flots store pages, null otherwise.
 */
export function appTargetOfUrl(url: string): AppLinkTarget | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.host === WEB_APP_HOST) return "web";
  if (parsed.host === "play.google.com" && parsed.searchParams.get("id") === "com.flots.app") return "android";
  if (parsed.host === "apps.apple.com" && parsed.pathname.includes("id6751442651")) return "ios";
  return null;
}

/**
 * Handoff query string for the app: consented session id, first-touch attribution, CTA
 * placement, landing path and context code. Empty values are omitted.
 */
function handoffParams(placement: string): URLSearchParams {
  const params = new URLSearchParams();
  const set = (key: string, value: string | null | undefined) => {
    if (value) params.set(key, value);
  };
  if (hasTrackingConsent()) set("fws", getOrCreateSessionUUID());
  const attribution = getAttribution();
  set("utm_source", attribution?.source);
  set("utm_medium", attribution?.medium);
  set("utm_campaign", attribution?.campaign);
  set("utm_content", attribution?.content);
  set("utm_term", attribution?.term);
  set("fpl", placement);
  set("flp", attribution?.landingPath);
  set("fctx", attribution?.context);
  return params;
}

/**
 * Builds the decorated URL of a CTA that opens the app or a store.
 *
 * - `web`: the web app URL (or `webBase` when it points at the web app) with `fws` (only with
 *   consent), `utm_*`, `fpl`, `flp`, `fctx`. A `webBase` on another host is returned untouched,
 *   so the session id never leaks to a third party.
 * - `android`: the Play Store URL with the same query string URL-encoded in `referrer=`
 *   (read by the app through the Play Install Referrer API).
 * - `ios`: the App Store URL with `ct=<placement>` and, when
 *   `NEXT_PUBLIC_APPSTORE_PROVIDER_TOKEN` is set, `pt=<token>`.
 *
 * Browser-only: on the server it returns the plain URL.
 *
 * @param target - App surface.
 * @param placement - Kebab-case CTA id.
 * @param webBase - Web-app URL to decorate instead of the default one (`web` only).
 * @returns The URL to put in the link.
 */
export function buildAppHref(target: AppLinkTarget, placement: string, webBase?: string): string {
  const plain = target === "web" ? (webBase ?? APP_URLS.web) : APP_URLS[target];
  if (typeof window === "undefined" || isCrawler()) return plain;

  if (target === "ios") {
    const url = new URL(plain);
    url.searchParams.set("ct", placement.slice(0, APPLE_CT_MAX));
    const providerToken = process.env.NEXT_PUBLIC_APPSTORE_PROVIDER_TOKEN;
    if (providerToken) url.searchParams.set("pt", providerToken);
    return url.toString();
  }

  const query = handoffParams(placement).toString();
  if (target === "android") return `${plain}&referrer=${encodeURIComponent(query)}`;

  let url: URL;
  try {
    url = new URL(plain);
  } catch {
    return plain;
  }
  if (url.host !== WEB_APP_HOST) return plain;
  for (const [key, value] of new URLSearchParams(query)) url.searchParams.set(key, value);
  return url.toString();
}

const CRAWLER_UA = /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|lighthouse|headless/i;

/**
 * True for search and AI crawlers. They render the page, so a link decorated client-side
 * (`?utm_*&fpl=…&flp=…`) becomes a distinct crawlable URL on the web app: Search Console
 * listed dozens of them as "page with redirect". Crawlers get the plain URL instead.
 */
function isCrawler(): boolean {
  return typeof navigator !== "undefined" && CRAWLER_UA.test(navigator.userAgent);
}

const noopSubscribe = () => () => {};

/**
 * Link of a CTA that opens the app: the plain URL while rendering on the server and hydrating
 * (crawlers and the first paint see a stable link, no hydration mismatch), the decorated one
 * right after, recomputed whenever the cookie choice changes.
 *
 * @param placement - Kebab-case CTA id sent as `fpl` / `ct`.
 * @param options.target - Force a surface; by default the visitor's platform (store on phones).
 * @param options.webBase - Web-app URL to use instead of the default one.
 * @returns The `href` to render, the undecorated URL (`plain`, safe to log) and the surface it opens.
 */
export function useAppLink(
  placement: string,
  options: { target?: AppLinkTarget; webBase?: string } = {},
): { href: string; plain: string; target: AppLinkTarget } {
  const { destination } = useAppTarget();
  const target = options.target ?? destination;
  const webBase = target === "web" ? options.webBase : undefined;
  const plain = target === "web" ? (webBase ?? APP_URLS.web) : APP_URLS[target];
  // Subscribing re-renders on accept/reject, which recomputes the snapshot below.
  useAppSelector((state) => state.cookieConsent.status);
  const href = useSyncExternalStore(
    noopSubscribe,
    () => buildAppHref(target, placement, webBase),
    () => plain,
  );
  return { href, plain, target };
}
